import React, { useState, useEffect, useCallback } from 'react';
import { GET, PATCH } from '../services/api';
import {
  UserPlus, Mail, Eye, CheckCircle, AlertTriangle, Info, XCircle, X
} from 'lucide-react';

// Join ABN lead statuses (value stored in the DB → label + badge colour).
// approved/rejected are legacy values that older applications may still carry.
const APPLICATION_STATUSES = [
  { value: 'pending', label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-100' },
  { value: 'interested', label: 'Interested', cls: 'bg-blue-50 text-blue-700 border-blue-100' },
  { value: 'follow-up', label: 'Follow-up', cls: 'bg-violet-50 text-violet-700 border-violet-100' },
  { value: 'not-interested', label: 'Not Interested', cls: 'bg-red-50 text-red-700 border-red-100' },
  { value: 'attended', label: 'Attended', cls: 'bg-sky-50 text-sky-700 border-sky-100' },
  { value: 'converted', label: 'Converted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
];
const LEGACY_STATUSES = {
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-100' },
};
const statusMeta = (status) =>
  APPLICATION_STATUSES.find((s) => s.value === status) || LEGACY_STATUSES[status] || APPLICATION_STATUSES[0];

const isSuperAdmin = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').role === 'super-admin'; } catch { return false; }
};

const StatusBadge = ({ status }) => {
  const meta = statusMeta(status);
  return <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${meta.cls}`}>{meta.label}</span>;
};

// Super-admin gets a dropdown; everyone else sees the badge.
const StatusControl = ({ app, canEdit, saving, onChange }) => {
  if (!canEdit) return <StatusBadge status={app.status} />;
  const meta = statusMeta(app.status);
  return (
    <select
      value={app.status || 'pending'}
      disabled={saving}
      onChange={(e) => onChange(app, e.target.value)}
      className={`px-2.5 py-1 text-xs font-bold rounded-full border outline-none cursor-pointer focus:ring-2 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-wait ${meta.cls}`}
    >
      {LEGACY_STATUSES[app.status] && <option value={app.status} disabled>{meta.label}</option>}
      {APPLICATION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
};

// --- Global Styles ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

// --- Shared Components ---
const CustomToast = ({ message, severity, onClose }) => {
  if (!message) return null;
  const styles = {
    success: { bg: 'bg-emerald-600', icon: <CheckCircle className="w-6 h-6" /> },
    info: { bg: 'bg-blue-600', icon: <Info className="w-6 h-6" /> },
    warn: { bg: 'bg-amber-500', icon: <AlertTriangle className="w-6 h-6" /> },
    error: { bg: 'bg-red-500', icon: <XCircle className="w-6 h-6" /> },
  };
  const style = styles[severity] || styles.info;
  return (
    <div className={`fixed bottom-6 right-6 z-[100] px-6 py-3 rounded-xl shadow-2xl text-white flex items-center gap-3 animate-fade-in-up ${style.bg}`}>
      {style.icon}
      <div className="flex-1"><div className="font-bold text-sm uppercase tracking-wide">{severity}</div><div className="text-sm font-medium">{message}</div></div>
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
    </div>
  );
};

// --- Modal for Contact Us Details ---
const ContactModal = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;
  const InfoBlock = ({ label, value }) => (<div className="bg-slate-50 p-3 rounded-xl border border-slate-100"><label className="block text-xs font-bold text-slate-400 uppercase mb-1">{label}</label><div className="text-sm font-semibold text-slate-800 break-words">{value}</div></div>);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in-up">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" /> Message Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><InfoBlock label="Sender Name" value={data.name} /><InfoBlock label="Sender Email" value={data.email} /></div>
          <InfoBlock label="Subject" value={data.subject} />
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><label className="block text-xs font-bold text-slate-400 uppercase mb-2">Message Content</label><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{data.message}</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between"><label className="text-xs font-bold text-slate-400 uppercase">Status</label><span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${data.status === 'read' ? 'bg-blue-100 text-blue-700' : data.status === 'replied' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>{data.status}</span></div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between"><label className="text-xs font-bold text-slate-400 uppercase">Date Received</label><span className="text-sm font-bold text-slate-700">{new Date(data.createdAt).toLocaleDateString()}</span></div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end">
          <button onClick={onClose} className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">Close</button>
        </div>
      </div>
    </div>
  );
};

// --- Modal for Join ABN Application Details ---
const ApplicationModal = ({ data, cityName, canEdit, saving, onStatusChange, onClose }) => {
  if (!data) return null;
  const InfoBlock = ({ label, value }) => (<div className="bg-slate-50 p-3 rounded-xl border border-slate-100"><label className="block text-xs font-bold text-slate-400 uppercase mb-1">{label}</label><div className="text-sm font-semibold text-slate-800 break-words">{value || '—'}</div></div>);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in-up">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" /> Application Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoBlock label="Full Name" value={data.fullName} />
            <InfoBlock label="Business Name" value={data.businessName} />
            <InfoBlock label="City" value={cityName} />
            <InfoBlock label="Industry Category" value={data.industryCategory} />
            <InfoBlock label="Phone Number" value={data.phoneNumber} />
            <InfoBlock label="Email" value={data.email} />
          </div>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100"><label className="block text-xs font-bold text-slate-400 uppercase mb-2">Business Description</label><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">{data.businessDescription || '—'}</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between gap-3"><label className="text-xs font-bold text-slate-400 uppercase">Status</label><StatusControl app={data} canEdit={canEdit} saving={saving} onChange={onStatusChange} /></div>
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between"><label className="text-xs font-bold text-slate-400 uppercase">Date Applied</label><span className="text-sm font-bold text-slate-700">{data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'N/A'}</span></div>
          </div>
        </div>
        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end">
          <button onClick={onClose} className="px-6 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">Close</button>
        </div>
      </div>
    </div>
  );
};

// --- Main Communications Component ---
const CommunicationsApp = () => {
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState('info');
  const [activeTab, setActiveTab] = useState('joinabn');

  // We fetch cities here too, to map City IDs to City Names in the Join ABN table
  const [cities, setCities] = useState([]);
  const [joinABNData, setJoinABNData] = useState([]);
  const [contactUsData, setContactUsData] = useState([]);

  const [isLoadingJoinABN, setIsLoadingJoinABN] = useState(false);
  const [isLoadingContactUs, setIsLoadingContactUs] = useState(false);

  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [savingStatusId, setSavingStatusId] = useState(null);
  const canEditStatus = isSuperAdmin();

  const showToast = useCallback((message, severity = 'info') => { setToastMessage(message); setToastSeverity(severity); setTimeout(() => setToastMessage(null), 3000); }, []);

  // Fetch Cities for ID->Name mapping
  const fetchCities = useCallback(async () => { try { const data = await GET('/settings/cities'); if (data) setCities(data); } catch (error) { console.error('Failed to fetch cities for mapping'); } }, []);
  const fetchJoinABNData = useCallback(async () => { setIsLoadingJoinABN(true); try { const result = await GET('/get-data-join-abn'); if (result?.success) setJoinABNData(result.data); } catch (error) { showToast('Error fetching Join ABN', 'error'); } finally { setIsLoadingJoinABN(false); } }, [showToast]);
  const fetchContactUsData = useCallback(async () => { setIsLoadingContactUs(true); try { const result = await GET('/get-all-contact-us'); if (result?.success) setContactUsData(result.data); } catch (error) { showToast('Error fetching Contact Us', 'error'); } finally { setIsLoadingContactUs(false); } }, [showToast]);

  const cityName = (app) => cities.find(c => c._id === app.city)?.name || app.city;
  const selectedApplication = joinABNData.find(a => a._id === selectedApplicationId) || null;

  const handleStatusChange = async (app, status) => {
    setSavingStatusId(app._id);
    try {
      const result = await PATCH(`/join-abn/${app._id}/status`, { status });
      setJoinABNData(prev => prev.map(a => (a._id === app._id ? { ...a, status: result?.data?.status || status } : a)));
      showToast(`Status changed to ${statusMeta(status).label}`, 'success');
    } catch (error) {
      showToast(error.message || 'Failed to update status', 'error');
    } finally {
      setSavingStatusId(null);
    }
  };

  useEffect(() => {
    fetchCities();
    fetchJoinABNData();
    fetchContactUsData();
  }, [fetchCities, fetchJoinABNData, fetchContactUsData]);

  const navItems = [
    { id: 'joinabn', label: 'Join ABN', icon: UserPlus },
    { id: 'contactus', label: 'Contact Us', icon: Mail },
  ];

  return (
    <div>
      <CustomStyles />
      <CustomToast message={toastMessage} severity={toastSeverity} onClose={() => setToastMessage(null)} />
      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} data={selectedContact} />
      <ApplicationModal
        data={selectedApplication}
        cityName={selectedApplication ? cityName(selectedApplication) : ''}
        canEdit={canEditStatus}
        saving={savingStatusId === selectedApplicationId}
        onStatusChange={handleStatusChange}
        onClose={() => setSelectedApplicationId(null)}
      />

      <div>
        {/* Navigation */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 mb-6 p-2 overflow-x-auto custom-scrollbar">
          <nav className="flex space-x-1 min-w-max">
            {navItems.map((item) => (
              <button key={item.id} onClick={() => setActiveTab(item.id)} className={`px-4 py-3 rounded-2xl text-sm font-bold flex items-center space-x-2 transition-all ${activeTab === item.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}>
                <item.icon className="w-4 h-4" /><span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 min-h-[500px]">

          {/* Join ABN Tab */}
          {activeTab === 'joinabn' && (
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 bg-white">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><UserPlus className="w-6 h-6 text-emerald-600" /> Join ABN Applications</h2>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50"><tr>{['Name', 'Business', 'City', 'Industry', 'Phone', 'Email', 'Description', 'Status', 'Date', 'Actions'].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>)}</tr></thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {isLoadingJoinABN ? <tr><td colSpan="10" className="text-center py-10 text-slate-400">Loading...</td></tr> :
                      joinABNData.length === 0 ? <tr><td colSpan="10" className="text-center py-10 text-slate-400">No applications found.</td></tr> :
                        joinABNData.map(app => (
                          <tr key={app._id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-800">{app.fullName}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{app.businessName}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{cityName(app)}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{app.industryCategory}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{app.phoneNumber}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{app.email}</td>
                            <td className="px-5 py-3 text-sm text-slate-500 max-w-xs truncate" title={app.businessDescription}>{app.businessDescription}</td>
                            <td className="px-5 py-3 whitespace-nowrap"><StatusControl app={app} canEdit={canEditStatus} saving={savingStatusId === app._id} onChange={handleStatusChange} /></td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-500">{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : 'N/A'}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm"><button onClick={() => setSelectedApplicationId(app._id)} title="View details" className="p-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"><Eye className="w-4 h-4" /></button></td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contact Us Tab */}
          {activeTab === 'contactus' && (
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 bg-white">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Mail className="w-6 h-6 text-sky-600" /> Contact Messages</h2>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50"><tr>{['Name', 'Email', 'Subject', 'Message', 'Status', 'Date', 'Actions'].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>)}</tr></thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {isLoadingContactUs ? <tr><td colSpan="7" className="text-center py-10 text-slate-400">Loading...</td></tr> :
                      contactUsData.length === 0 ? <tr><td colSpan="7" className="text-center py-10 text-slate-400">No messages found.</td></tr> :
                        contactUsData.map(msg => (
                          <tr key={msg._id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-800">{msg.name}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{msg.email}</td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-600">{msg.subject}</td>
                            <td className="px-5 py-3 text-sm text-slate-500 max-w-xs truncate">{msg.message}</td>
                            <td className="px-5 py-3 whitespace-nowrap"><span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${msg.status === 'read' ? 'bg-blue-50 text-blue-700 border-blue-100' : msg.status === 'replied' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-700 border-slate-100'}`}>{msg.status}</span></td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm text-slate-500">
                              {new Date(msg.createdAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm"><button onClick={() => { setSelectedContact(msg); setShowContactModal(true); }} className="p-2 text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors"><Eye className="w-4 h-4" /></button></td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CommunicationsApp;